import { runAnalytics } from "../../utils/runAnalytics";
import { getStapeAttributionFromSearch, captureStapeAttribution } from "../../utils/stapePurchase";
import { useTrialPaymentAttempt, isDefiniteTrialFailure } from "../../hooks/useTrialPaymentAttempt";
import { TrialPaymentNotice } from "../TrialPaymentTerms/TrialPaymentTerms";
import React, { useState, useEffect, useRef } from "react";
import { createWalletInteractionAnalytics } from "../../utils/walletInteractionAnalytics";
import { PaymentRequestButtonElement, useStripe } from "@stripe/react-stripe-js";
import { useDispatch } from "react-redux";
import { setApplePayAvailible } from "../../store/reducers/payment";
import { API_URL, EU_STRIPE_PRICE_ID, TRIAL_DAYS, STRIPE_PRICE_ID } from "../../constants/formConstants";
import axios from "axios";
import { getAcquisitionCountryMetadataFromSearch } from "../../utils/acquisitionCountry";
import { getCheckoutMetadataFromSearch as getUtmMetadataFromSearch } from "../../utils/checkoutMetadata";
import { getPaymentOfferId } from "../../utils/paywallPricing";
import { getPaymentLimitMessage } from "../../utils/paymentLimitMessage";
import { pushPurchaseEvent } from "../../utils/gtm";
import { getClientReferenceId, sendAmplitudePurchase } from "../../utils/amplitude";
import { DEFAULT_WALLET_PAYMENT_LABEL, getPaywallContext } from "../../utils/paywallContext";
import {
  getWalletAvailabilityPayload,
  sendPaywallEvent,
} from "../../utils/paywallAnalytics";

const AppleGooglePayButton = ({
  trialCost,
  paymentAmount,
  paymentCurrency = "usd",
  paymentValue,
  trialOffer,
  onSuccess,
  isDisabled = false,
}) => {
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentRequestKey, setPaymentRequestKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const stripe = useStripe();
  const dispatch = useDispatch();

  const clientReferenceId = getClientReferenceId();
  const pathname = window.location?.pathname || "";
  const { paywall, paymentLabel } = getPaywallContext(pathname);
  const walletPaymentLabel = paymentLabel || DEFAULT_WALLET_PAYMENT_LABEL;
  const utmMetadata = getUtmMetadataFromSearch(window.location.search, { maxKeys: 45 });
  const stapeAttribution = getStapeAttributionFromSearch(window.location.search);
  const paymentCurrencyCode = trialOffer?.offer?.currency || String(paymentCurrency || "usd").toLowerCase();
  const metadataPriceId =
    paymentCurrencyCode === "eur" ? EU_STRIPE_PRICE_ID : STRIPE_PRICE_ID;
  const purchaseValue = trialOffer?.offer ? trialOffer.offer.initialAmount / 100 : Number.isFinite(Number(paymentValue))
    ? Number(paymentValue)
    : Number(trialCost);
  const computedPaymentAmount = trialOffer?.offer?.initialAmount ?? (Number.isFinite(Number(paymentAmount))
    ? Math.round(Number(paymentAmount))
    : Math.round(Number(trialCost) * 100));
  const offerId = trialOffer?.offer?.offerId || getPaymentOfferId(pathname, window.location.search, computedPaymentAmount, paymentCurrencyCode);
  const isSplit = trialOffer?.offer?.paymentMode === "split_trial_v1";
  const trialCampaign = trialOffer?.offer?.trialCampaign;
  const offerReady = !trialOffer || trialOffer.ready;
  const attempt = useTrialPaymentAttempt(trialOffer, { amount: computedPaymentAmount, currency: paymentCurrencyCode });
  const walletInteractionRef = useRef(new WeakMap());
  const { run: runAttempt, cancel: cancelAttempt, success: markSuccess, hold: holdAttempt } = attempt;
  const confirmedStatus = status => status === "succeeded" || (!isSplit && ["processing", "requires_capture"].includes(status));
  const finishPending = async (methodId, path, paymentIntent, acquisitionContext = stapeAttribution) => {
    markSuccess();
    try { sessionStorage.setItem("stripePaymentMethodId", methodId); } catch (_) {}
    persistTrackingContext();
    sendPaywallEvent("trial_purchase_completed", { paymentPath: path, payload: {
      currency: paymentCurrencyCode, amount: computedPaymentAmount, status: "succeeded" } });
    try { runAnalytics(() => pushPurchaseEvent({ clientReferenceId, paywall, value: purchaseValue, currency: paymentCurrencyCode, type: "trial", transactionId: paymentIntent?.id, paymentStatus: paymentIntent?.status, acquisitionContext })); } catch (_) {}
    try { runAnalytics(() => sendAmplitudePurchase({ userId: clientReferenceId, revenue: purchaseValue, currency: paymentCurrencyCode,
      product: "Trial payment", paywall, type: "trial" })); } catch (_) {}
    onSuccess?.();
  };
  const persistTrackingContext = () => {
    try {
      if (paywall) {
        sessionStorage.setItem("lastPaywall", paywall);
      }
      if (clientReferenceId) {
        sessionStorage.setItem("clientReferenceId", clientReferenceId);
      }
    } catch (_) {}
  };

  useEffect(() => {
    sendPaywallEvent("payment_options_seen", {
      paymentPath: "wallet",
      payload: {
        currency: paymentCurrencyCode,
        amount: computedPaymentAmount,
      },
    });
  }, [paymentCurrencyCode, computedPaymentAmount]);

  const options = {
    paymentRequest,
    style: {
      paymentRequestButton: {
        type: "default",
        theme: "dark",
        height: "55px",
      },
    },
  };

  useEffect(() => {
    setPaymentRequest(null);
    if (!stripe || !trialCost || !offerReady) return;
    let cancelled = false;

    const amount = computedPaymentAmount;
    if (!amount || amount <= 0) return;

    const walletInteraction = createWalletInteractionAnalytics({
      amount, currency: paymentCurrencyCode, offer_id: offerId,
      wallet_surface: "standalone_button",
    });
    const onWalletCancel = () => {
      cancelAttempt();
      walletInteraction.dismissed();
    };
    const pr = stripe.paymentRequest({
      country: "US",
      currency: paymentCurrencyCode,
      total: {
        label: walletPaymentLabel,
        amount,
      },
      requestPayerEmail: true,
      requestPayerName: true,
    });

    walletInteractionRef.current.set(pr, walletInteraction);
    pr.canMakePayment()
      .then((res) => {
        if (cancelled) return;
        walletInteraction.available(res);
        if (res) {
          sendPaywallEvent("wallet_available", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              ...getWalletAvailabilityPayload(res),
            },
          });
          dispatch(setApplePayAvailible(true));
          setPaymentRequest(pr);
          setPaymentRequestKey((prev) => prev + 1);
        } else {
          sendPaywallEvent("wallet_unavailable", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              payment_request_supported: false,
            },
          });
          dispatch(setApplePayAvailible(false));
        }
      })
      .catch(() => {
        if (cancelled) return;
        sendPaywallEvent("wallet_unavailable", {
          paymentPath: "wallet",
          payload: {
            currency: paymentCurrencyCode,
            amount,
            reason: "can_make_payment_error",
          },
        });
        dispatch(setApplePayAvailible(false));
      });

    const onPaymentMethod = async (e) => {
      if (cancelled) { e.complete("fail"); return; }
      const accepted = await runAttempt(async () => {
      // A wallet handler can outlive URL navigation. Freeze this attempt's attribution now.
      const clientReferenceId = getClientReferenceId();
      const utmMetadata = getUtmMetadataFromSearch(window.location.search, { maxKeys: 45 });
      const stapeAttribution = captureStapeAttribution(window.location.search);
      walletInteraction.authorized(e?.walletName);
      setLoading(true);
      setErrorMessage(""); // Clear previous errors
      const now = new Date();
      now.setDate(now.getDate() + TRIAL_DAYS);
      const trialEnd = Math.floor(now.valueOf() / 1000);

      // SUBSCRIPTION CREATION DISABLED - Use payment-only endpoint
      let existingCustomerId = null;
      try {
        existingCustomerId = sessionStorage.getItem("stripeCustomerId");
      } catch {}

      const acquisitionCountryMetadata = getAcquisitionCountryMetadataFromSearch(
        window.location.search,
      );
      const bodyObj = {
        ...(offerId ? { offerId } : {}),
        ...(trialOffer?.offer ? { trialCampaign } : {}),
        amount,
        id: e.paymentMethod.id,
        email: e.payerEmail || "applegoogle@email.com",
        data: {
          currency: paymentCurrencyCode,
          client_reference_id: clientReferenceId,
          price_id: metadataPriceId,
          ...utmMetadata,
          ...acquisitionCountryMetadata,
          ...(paywall ? { paywall } : {}),
        },
        ...(existingCustomerId ? { customerId: existingCustomerId } : {}),
      };

      try {
        // Use payment endpoint instead of create-payment-intent to avoid subscriptions
        const idempotencyKey = `${e.paymentMethod.id}-${amount}`;
        const response = await axios.post(`${API_URL}/payment`, bodyObj, {
          headers: { "Idempotency-Key": idempotencyKey },
        });
        const returnedCustomerId =
          response?.data?.customerId || response?.data?.customer?.id;
        if (returnedCustomerId) {
          try {
            sessionStorage.setItem("stripeCustomerId", returnedCustomerId);
          } catch {}
        }
        
        if (response.data.success) {
          markSuccess();
          try {
            sessionStorage.setItem("stripePaymentMethodId", e.paymentMethod.id);
          } catch {}
          e.complete("success");
          setLoading(false);
          persistTrackingContext();
          sendPaywallEvent("trial_purchase_completed", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              status: "succeeded",
            },
          });
          runAnalytics(() => pushPurchaseEvent({
            clientReferenceId,
            paywall,
            value: purchaseValue,
            currency: paymentCurrencyCode,
            type: "trial",
            acquisitionContext: stapeAttribution,
            transactionId: response.data.id,
            paymentStatus: "succeeded",
          }));
          runAnalytics(() => sendAmplitudePurchase({
            userId: clientReferenceId,
            revenue: purchaseValue,
            currency: paymentCurrencyCode,
            product: "Trial payment",
            paywall,
            type: "trial",
          }));
          if (onSuccess) onSuccess();
        } else if (response.data.requires_action) {
          // Close wallet sheet before starting 3DS
          e.complete("success");
          sendPaywallEvent("wallet_payment_requires_action", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              error_stage: "confirm_card_payment",
              status: "requires_action",
            },
          });
          try {
            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
              response.data.client_secret
            );
            if (isSplit && (confirmError ? !["requires_payment_method", "canceled"].includes(confirmError.payment_intent?.status) : !["succeeded", "requires_payment_method", "canceled"].includes(paymentIntent?.status))) {
              holdAttempt(response.data.client_secret, paymentIntent => finishPending(e.paymentMethod.id, "wallet", paymentIntent, stapeAttribution), { paymentPath: "wallet", reason: "confirmation_unknown" });
              setLoading(false); return;
            }
            if (confirmError) {
              setLoading(false);
              setErrorMessage(confirmError.message);
              sendPaywallEvent("wallet_payment_failed", {
                paymentPath: "wallet",
                payload: {
                  currency: paymentCurrencyCode,
                  amount,
                  error_stage: "confirm_card_payment",
                },
              });
            } else {
              const status = paymentIntent && paymentIntent.status;
              if (
                confirmedStatus(status)
              ) {
                markSuccess();
                try {
                  sessionStorage.setItem("stripePaymentMethodId", e.paymentMethod.id);
                } catch {}
                setLoading(false);
                persistTrackingContext();
                sendPaywallEvent("trial_purchase_completed", {
                  paymentPath: "wallet",
                  payload: {
                    currency: paymentCurrencyCode,
                    amount,
                    status,
                  },
                });
                runAnalytics(() => pushPurchaseEvent({
                  clientReferenceId,
                  paywall,
                  value: purchaseValue,
            currency: paymentCurrencyCode,
                  type: "trial",
                  acquisitionContext: stapeAttribution,
                  transactionId: paymentIntent.id,
                  paymentStatus: status,
                }));
                runAnalytics(() => sendAmplitudePurchase({
                  userId: clientReferenceId,
                  revenue: purchaseValue,
                  currency: paymentCurrencyCode,
                  product: "Trial payment",
                  paywall,
                  type: "trial",
                }));
                if (onSuccess) onSuccess();
              } else {
                setLoading(false);
                setErrorMessage(
                  "Payment authentication failed. Please try again."
                );
                sendPaywallEvent("wallet_payment_failed", {
                  paymentPath: "wallet",
                  payload: {
                    currency: paymentCurrencyCode,
                    amount,
                    error_stage: "confirm_card_payment",
                    status,
                  },
                });
              }
            }
          } catch (err) {
            if (attempt.succeeded()) return;
            if (isSplit) { holdAttempt(response.data.client_secret, paymentIntent => finishPending(e.paymentMethod.id, "wallet", paymentIntent, stapeAttribution), { paymentPath: "wallet", reason: "confirmation_unknown" }); setLoading(false); return; }
            setLoading(false);
            setErrorMessage("Payment authentication failed. Please try again.");
            sendPaywallEvent("wallet_payment_failed", {
              paymentPath: "wallet",
              payload: {
                currency: paymentCurrencyCode,
                amount,
                error_stage: "confirm_card_payment",
              },
            });
          }
        } else {
          if (isSplit && !isDefiniteTrialFailure(response.data)) {
            e.complete("success"); holdAttempt(response.data.client_secret, paymentIntent => finishPending(e.paymentMethod.id, "wallet", paymentIntent, stapeAttribution), { paymentPath: "wallet", reason: "status_unknown" });
            setLoading(false); return;
          }
          e.complete("fail");
          setLoading(false);
          setErrorMessage(response.data.message || "Payment failed. Please try again.");
          sendPaywallEvent("wallet_payment_failed", {
            paymentPath: "wallet",
            payload: {
              currency: paymentCurrencyCode,
              amount,
              error_stage: "payment_endpoint",
            },
          });
        }
      } catch (error) {
        if (attempt.succeeded()) return;
        if (attempt.changed(error)) { e.complete("fail"); setLoading(false); setErrorMessage("Price updated. Review the terms and pay again."); return; }
        if (isSplit && (!error.response || error.response.status >= 500)) holdAttempt(null, null, { paymentPath: "wallet", reason: "response_unknown" });
        e.complete("fail");
        setLoading(false);
        setErrorMessage(getPaymentLimitMessage(error) || "Payment failed. Please try again.");
        sendPaywallEvent("wallet_payment_failed", {
          paymentPath: "wallet",
          payload: {
            currency: paymentCurrencyCode,
            amount,
            error_stage: "payment_endpoint",
          },
        });
      }
      }, true);
      if (!accepted) e.complete("fail");
    };
    pr.on("paymentmethod", onPaymentMethod);
    pr.on("cancel", onWalletCancel);
    return () => { cancelled = true; pr.off?.("paymentmethod", onPaymentMethod); pr.off?.("cancel", onWalletCancel); };
  }, [stripe, trialCost, computedPaymentAmount, paymentCurrencyCode, metadataPriceId, purchaseValue, walletPaymentLabel, offerId, trialCampaign, offerReady, runAttempt, cancelAttempt]);

  if (!paymentRequest || !offerReady) return <TrialPaymentNotice controller={trialOffer} attempt={attempt} stripe={stripe} />;

  return (
    <>
    <TrialPaymentNotice controller={trialOffer} attempt={attempt} stripe={stripe} />
    <div
      className={`mb-4 ${isDisabled || trialOffer?.busy || attempt.pending ? "opacity-50 pointer-events-none" : ""} ${
        loading ? "opacity-50 pointer-events-none" : ""
      }`}
    >
      <div
        key={paymentRequestKey}
        style={{
          marginBottom: 8,
          overflow: "hidden",
          borderRadius: 8,
        }}
      >
        <PaymentRequestButtonElement
          onClick={event => {
            if (attempt.click(event)) walletInteractionRef.current.get(paymentRequest)?.clicked();
          }}
          options={options}
          className="apple-google-pay"
        />
      </div>
      {errorMessage && (
        <div role="alert" className="mt-2 text-red-600 text-sm text-center">
          {errorMessage}
        </div>
      )}
    </div>
    </>
  );
};

export default AppleGooglePayButton;
